import type { User } from "firebase/auth";
import { useEffect, useState } from "react";
import { readingTime } from "./format";
import {
  type Collector,
  type CollectorPick,
  type Competitor,
  countLines,
  errorText,
  issueGroups,
  issueText,
  lockSalesImport,
  mapCollectors,
  mapEntries,
  missingRoute,
  pickOf,
  runSalesImport,
  type SalesOverview,
  type SalesRunDetail,
  salesOverview,
} from "./salesImport";

const ISSUES_SHOWN = 200;
const MODE_TEXT = { "dry-run": "Preview (dry run)", apply: "Apply" } as const;
const STATE_TEXT = { ok: "OK", refused: "Refused", failed: "Failed" } as const;

/** Design 26.11 F7: preview, apply and lock the sales final import. Only an administrator sees it. */
export function SalesImportPanel({ user, timeZone }: { user: User; timeZone: string }) {
  const [found, setFound] = useState<SalesOverview | "missing" | "error" | undefined>(undefined);
  const [run, setRun] = useState<SalesRunDetail | null>(null);
  const [busy, setBusy] = useState<"dry-run" | "apply" | "lock" | null>(null);
  const [confirm, setConfirm] = useState<"apply" | "lock" | null>(null);
  const [status, setStatus] = useState<{ ok: boolean; text: string } | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let live = true;
    user
      .getIdToken()
      .then(salesOverview)
      .then((overview) => {
        if (!live) return;
        setFound(overview);
        setRun(overview.newest);
      })
      .catch((e) => {
        if (!live) return;
        console.info(`sales import: the overview did not load (${missingRoute(e) ? "no route" : "error"})`);
        setFound(missingRoute(e) ? "missing" : "error");
      });
    return () => {
      live = false;
    };
  }, [user, attempt]);
  if (found === "missing" || found === undefined) return null;
  if (found === "error") {
    return (
      <section aria-labelledby="sales-import-title" data-testid="sales-import">
        <h2 id="sales-import-title">Sales final import</h2>
        <p role="alert" className="error" data-testid="sales-import-error">
          The sales import did not load.{" "}
          <button type="button" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </button>
        </p>
      </section>
    );
  }
  const start = async (mode: "dry-run" | "apply") => {
    setBusy(mode);
    setConfirm(null);
    setStatus(null);
    const began = performance.now();
    try {
      const done = await runSalesImport(await user.getIdToken(), mode);
      console.info(`sales import: ${mode} ${done.state} in ${Math.round(performance.now() - began)} ms`);
      setRun(done);
      setStatus(done.state === "ok" ? { ok: true, text: `${MODE_TEXT[mode]} done.` } : { ok: false, text: `${STATE_TEXT[done.state]}: ${done.refused}` });
      setAttempt((n) => n + 1);
    } catch (e) {
      setStatus({ ok: false, text: errorText(e) });
    } finally {
      setBusy(null);
    }
  };
  const lock = async () => {
    setBusy("lock");
    setConfirm(null);
    try {
      await lockSalesImport(await user.getIdToken());
      setStatus({ ok: true, text: "The sales import is locked." });
      setAttempt((n) => n + 1);
    } catch (e) {
      setStatus({ ok: false, text: errorText(e) });
    } finally {
      setBusy(null);
    }
  };
  const canRun = found.can_edit && found.source !== null;
  const applied = found.runs.some((r) => r.mode === "apply" && r.state === "ok");
  return (
    <section aria-labelledby="sales-import-title" data-testid="sales-import" data-locked={found.locked ? "true" : "false"}>
      <h2 id="sales-import-title">Sales final import</h2>
      <p className="sub">
        One import of the contacts, the payees, the contract terms, the notes and the check-ins from the sales source. Preview it, apply it, and run it
        again until the data is correct. Then lock it: after the lock the import writes no sales field, and each difference shows as an issue. The import
        never reads a bank column.
      </p>
      {found.source === null ? (
        <p data-testid="sales-import-none">This organization has no sales source that is on. An operator sets the source.</p>
      ) : (
        <p data-testid="sales-import-source">
          Source: {found.source.label}
        </p>
      )}
      {found.locked && (
        <p className="chip-flag long" data-testid="sales-import-locked">
          Locked {readingTime(found.locked.at, timeZone)} by {found.locked.by}
        </p>
      )}
      {canRun && (
        <div className="actions">
          <button type="button" className="primary" data-testid="sales-import-preview" disabled={busy !== null} onClick={() => start("dry-run")}>
            {busy === "dry-run" ? "Reading the source…" : "Preview (dry run)"}
          </button>
          {!found.locked && (
            <button type="button" data-testid="sales-import-apply" disabled={busy !== null} onClick={() => setConfirm("apply")}>
              {busy === "apply" ? "Applying…" : "Apply"}
            </button>
          )}
          {!found.locked && applied && (
            <button type="button" data-testid="sales-import-lock" disabled={busy !== null} onClick={() => setConfirm("lock")}>
              {busy === "lock" ? "Locking…" : "Lock the import"}
            </button>
          )}
        </div>
      )}
      {confirm && (
        <div className="confirm" role="group" aria-label="Confirm" data-testid="sales-import-confirm">
          <p>
            {confirm === "apply"
              ? "Apply writes the sales fields of each matched account. It replaces the rows of the last apply."
              : "After the lock the import never writes a sales field again. You cannot undo the lock on this page."}
          </p>
          <button type="button" className="primary" data-testid="sales-import-confirm-yes" onClick={() => (confirm === "apply" ? start("apply") : lock())}>
            {confirm === "apply" ? "Apply now" : "Lock now"}
          </button>
          <button type="button" onClick={() => setConfirm(null)}>
            Cancel
          </button>
        </div>
      )}
      <p role="status" data-testid="sales-import-status" className={status && !status.ok ? "error" : undefined}>
        {status?.text}
      </p>
      {run ? <RunDetail run={run} timeZone={timeZone} /> : found.source && <p data-testid="sales-import-empty">No preview yet.</p>}
      {run && run.collectors.length > 0 && (
        <CollectorMap
          key={run.id}
          user={user}
          collectors={run.collectors}
          competitors={found.competitors}
          canEdit={found.can_edit}
          onSaved={() => setAttempt((n) => n + 1)}
        />
      )}
      {found.runs.length > 1 && (
        <details data-testid="sales-import-runs">
          <summary>Earlier runs ({found.runs.length})</summary>
          <ul>
            {found.runs.map((r) => (
              <li key={r.id}>
                {readingTime(r.started_at, timeZone)} · {MODE_TEXT[r.mode]} · {STATE_TEXT[r.state]}
                {r.counts.accounts_changed !== undefined && ` · ${r.counts.accounts_changed} accounts change`} · {r.actor}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

function RunDetail({ run, timeZone }: { run: SalesRunDetail; timeZone: string }) {
  const [code, setCode] = useState<string | null>(null);
  const groups = issueGroups(run.issues);
  const shown = run.issues.filter((issue) => code === null || issue.code === code).slice(0, ISSUES_SHOWN);
  return (
    <div data-testid="sales-import-run" data-mode={run.mode} data-state={run.state}>
      <h3>
        {MODE_TEXT[run.mode]}, {readingTime(run.started_at, timeZone)}: {STATE_TEXT[run.state]}
      </h3>
      {run.refused && <p className="error">{run.refused}</p>}
      <dl className="facts" data-testid="sales-import-counts">
        {countLines(run.counts).map(([key, label, value]) => (
          <div key={key} data-count={key}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
      {groups.length > 0 ? (
        <>
          <h4>Issues</h4>
          <ul className="chips" data-testid="sales-import-issue-codes">
            <li>
              <button type="button" aria-pressed={code === null} onClick={() => setCode(null)}>
                All ({run.issues.length})
              </button>
            </li>
            {groups.map((group) => (
              <li key={group.code}>
                <button type="button" aria-pressed={code === group.code} onClick={() => setCode(group.code)}>
                  {issueText(group.code)} ({group.count})
                </button>
              </li>
            ))}
          </ul>
          <div className="table-scroll" tabIndex={0} role="region" aria-label="Sales import issues">
            <table className="list" data-testid="sales-import-issues">
              <thead>
                <tr>
                  <th scope="col">Issue</th>
                  <th scope="col">Account</th>
                  <th scope="col">Row</th>
                  <th scope="col">Column</th>
                  <th scope="col">Value</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((issue, n) => (
                  <tr key={`${issue.code}-${issue.row}-${n}`}>
                    <th scope="row">{issueText(issue.code)}</th>
                    <td data-label="Account">{issue.number ?? "–"}</td>
                    <td data-label="Row">{issue.row ?? "–"}</td>
                    <td data-label="Column">{issue.column || "–"}</td>
                    <td data-label="Value">{issue.value ?? "–"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {shown.length >= ISSUES_SHOWN && <p className="sub">The first {ISSUES_SHOWN} issues show. Pick a code to see others.</p>}
        </>
      ) : (
        <p data-testid="sales-import-no-issues">No issues.</p>
      )}
    </div>
  );
}

const pickValue = (pick: CollectorPick) => (pick.kind === "competitor" ? pick.id : pick.kind);

function CollectorMap({
  user,
  collectors,
  competitors,
  canEdit,
  onSaved,
}: {
  user: User;
  collectors: Collector[];
  competitors: Competitor[];
  canEdit: boolean;
  onSaved: () => void;
}) {
  const [picks, setPicks] = useState<Record<string, CollectorPick>>(() => Object.fromEntries(collectors.map((c) => [c.key, pickOf(c)])));
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<{ ok: boolean; text: string } | null>(null);
  const entries = mapEntries(collectors, picks);
  const save = async () => {
    setSaving(true);
    setStatus(null);
    try {
      const done = await mapCollectors(await user.getIdToken(), entries);
      console.info(`sales import: collector map saved ${JSON.stringify(done.counts)}`);
      setStatus({ ok: true, text: `${entries.length} names saved. Preview again to see the result.` });
      onSaved();
    } catch (e) {
      setStatus({ ok: false, text: errorText(e) });
    } finally {
      setSaving(false);
    }
  };
  return (
    <div data-testid="sales-import-collectors">
      <h3>Previous collectors</h3>
      <p className="sub">Give each name of the source its company in the competitor list. The contract keeps the competitor.</p>
      <div className="table-scroll" tabIndex={0} role="region" aria-label="Previous collectors">
        <table className="list">
          <thead>
            <tr>
              <th scope="col">Name in the source</th>
              <th scope="col">Rows</th>
              <th scope="col">Competitor</th>
            </tr>
          </thead>
          <tbody>
            {collectors.map((collector) => {
              const pick = picks[collector.key] ?? pickOf(collector);
              const id = `collector-${collector.key.replaceAll(" ", "-")}`;
              return (
                <tr key={collector.key} data-testid="sales-import-collector">
                  <th scope="row">
                    <label htmlFor={id}>{collector.name}</label>
                  </th>
                  <td data-label="Rows">{collector.rows}</td>
                  <td data-label="Competitor">
                    <select
                      id={id}
                      disabled={!canEdit}
                      value={pickValue(pick)}
                      onChange={(e) => {
                        const value = e.target.value;
                        const next: CollectorPick =
                          value === "none" ? { kind: "none" } : value === "ignore" ? { kind: "ignore" } : value === "new" ? { kind: "new", name: collector.name } : { kind: "competitor", id: value };
                        setPicks((old) => ({ ...old, [collector.key]: next }));
                      }}
                    >
                      <option value="none">Choose</option>
                      {competitors.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                      <option value="new">New competitor…</option>
                      <option value="ignore">Not a competitor</option>
                    </select>
                    {pick.kind === "new" && (
                      <input
                        aria-label={`New competitor for ${collector.name}`}
                        value={pick.name}
                        maxLength={200}
                        onChange={(e) => setPicks((old) => ({ ...old, [collector.key]: { kind: "new", name: e.target.value } }))}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {canEdit && (
        <div className="actions">
          <button type="button" data-testid="sales-import-collectors-save" disabled={saving || entries.length === 0} onClick={save}>
            {saving ? "Saving…" : entries.length ? `Save ${entries.length} changes` : "No changes to save"}
          </button>
        </div>
      )}
      <p role="status" className={status && !status.ok ? "error" : undefined}>
        {status?.text}
      </p>
    </div>
  );
}
